import { ProfileRemove, Slash } from 'iconsax-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useOnClickOutside } from 'usehooks-ts';
import MoreIcon from '@/assets/images/icons/more.svg?react';
import useAddBlackList from '@/hooks/users/use-add-blacklist';
import { useBlacklisted } from '@/hooks/users/use-blacklist';
import useDeleteBlacklist from '@/hooks/users/use-delete-blacklist';
import useDeleteFriend from '@/hooks/users/use-delete-friend';
import ROUTES from '@/routes';
import { makePrompt } from '@/stores/prompter';
import cn from '@/utils/merge-class-names';

interface Props {
  userId: number | undefined;
  navigateBack: 'contact' | 'session' | undefined;
}

export default function UserHeaderMenu({ userId, navigateBack }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: isBlacklisted } = useBlacklisted(userId);
  const { mutateAsync: blacklistUser } = useAddBlackList();
  const { mutateAsync: deleteBlacklist } = useDeleteBlacklist();
  const { mutateAsync: deleteFriend } = useDeleteFriend();
  const menuRef = useRef<HTMLUListElement>(null);
  const [showMenu, setShowMenu] = useState(false);

  useOnClickOutside(menuRef, () => setTimeout(() => setShowMenu(false), 50));

  const handleDeleteFriend = async () => {
    if (!userId) return;

    await makePrompt({
      key: 'delete-friend',
      type: 'dialog',
      title: t('user.menu.delete'),
      content: t('user.menu.delete-confirm'),
      promise: async () => {
        const result = await deleteFriend(userId);

        if (result.Code === 0) {
          switch (navigateBack) {
            case 'contact':
              navigate(ROUTES.CONTACT.$path(), { replace: true });
              break;
            case 'session':
              navigate(ROUTES.MESSAGES.$path(), { replace: true });
              break;
            default:
              navigate(-1);
              break;
          }
        }
      },
    });
  };

  const handleBlacklistUser = async () => {
    userId &&
      (await makePrompt({
        key: 'blacklist-user',
        type: 'dialog',
        title: t('user.menu.blacklist.block'),
        content: t('user.menu.blacklist.confirm'),
        promise: () => blacklistUser(userId),
      }));
  };

  return (
    <div className='relative'>
      <button
        type='button'
        className={cn('btn btn-square btn-ghost mode-embedded:btn-sm', { shadow: showMenu })}
        onClick={() => setShowMenu(!showMenu)}>
        <MoreIcon className='size-6 rotate-90 p-0.5' />
      </button>

      {showMenu && (
        <ul
          ref={menuRef}
          tabIndex={0}
          className='menu absolute right-0 z-100 min-w-40 rounded-box bg-neutral-0 shadow text-body'>
          {isBlacklisted ? (
            <li tabIndex={0}>
              <button
                type='button'
                className='flex w-full rounded-box items-center gap-2'
                onClick={() => userId && deleteBlacklist(userId)}>
                <Slash className='size-5' />
                {t('user.menu.blacklist.unblock')}
              </button>
            </li>
          ) : (
            <li tabIndex={0}>
              <button
                type='button'
                className='flex w-full rounded-box items-center gap-2'
                onClick={handleBlacklistUser}>
                <Slash className='size-5' />
                {t('user.menu.blacklist.block')}
              </button>
            </li>
          )}

          <li tabIndex={0}>
            <button
              type='button'
              className='flex w-full rounded-box items-center gap-2'
              onClick={handleDeleteFriend}>
              <ProfileRemove className='size-5' />
              {t('user.menu.delete')}
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}
